/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import querystring from 'querystring';
import { IGridSettings } from '../../contract/IGridSettings';
import { IImageData } from '../../interfaces/data-types/IImageData';
import { IImageDimension, IImageSettings } from '../../interfaces/data-types/IImageSettings';
import { Viewport } from './image-types/native-image';

const CROP_FOCAL_REGION = 'cropfocalregion';

export const getLargestGridSettingViewportName = (gridSettings: IGridSettings): Viewport => {
    return (
        (gridSettings &&
            ((gridSettings.xl && 'xl') ||
                (gridSettings.lg && 'lg') ||
                (gridSettings.md && 'md') ||
                (gridSettings.sm && 'sm') ||
                (gridSettings.xs && 'xs'))) ||
        'xl'
    );
};

const formatMediaQuery = (max?: number, min?: number) =>
    `${min ? `(min-width:${min}px)${max ? ' and ' : ''}` : ''}${max ? `(max-width:${max}px)` : ''}`;

const getMedia = (
    gridSettings: IGridSettings,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport
) => {
    const { max, min } = getMinMaxWidth(gridSettings, imageSettings, viewport, largestGridSettingViewportName);
    return formatMediaQuery(max, min);
};

export const getMinMaxWidth = (
    gridSettings: IGridSettings,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport
): { max?: number; min?: number } => {
    const gridSetting = gridSettings[viewport];
    const max: number | undefined = gridSetting && gridSetting.w;
    if (!max) {
        return {};
    }
    // find first setting smaller than max
    const min = [
        imageSettings.viewports.xl && gridSettings.xl && gridSettings.xl.w,
        imageSettings.viewports.lg && gridSettings.lg && gridSettings.lg.w,
        imageSettings.viewports.md && gridSettings.md && gridSettings.md.w,
        imageSettings.viewports.sm && gridSettings.sm && gridSettings.sm.w,
        imageSettings.viewports.xs && gridSettings.xs && gridSettings.xs.w
    ]
        .filter(Boolean)
        .find(setting => setting && setting < max);
    return { max: viewport !== largestGridSettingViewportName ? max : undefined, min: min ? min + 1 : min };
};

export const getImageResizeUrl = (
    imageSrc: string | undefined,
    vpSetting: IImageDimension | undefined,
    inputImageSettings?: IImageSettings | undefined,
    skipWidthAndHeightSettings?: boolean
): string => {
    if (!imageSrc) {
        return '';
    }
    if (imageSrc.startsWith('svg.')) {
        return imageSrc;
    }

    try {
        const srcUrl = new URL(imageSrc);
        let queryString;

        if (vpSetting && vpSetting.q) {
            queryString = vpSetting.q;
        } else {
            // Reading mode value from image Settings
            queryString = 'm=6';

            if (vpSetting && vpSetting.w && vpSetting.h) {
                queryString = `${queryString}&w=${vpSetting.w}&h=${vpSetting.h}`;
            }

            if (inputImageSettings && inputImageSettings.quality) {
                queryString = `${queryString}&q=${inputImageSettings.quality}`;
            }
        }

        // We use sitebuilder configurations as an override
        if (inputImageSettings) {
            queryString = updateImageResizerQueryString(queryString, inputImageSettings);
        }

        if (inputImageSettings && inputImageSettings.cropFocalRegion) {
            queryString = `${queryString}&${CROP_FOCAL_REGION}=true`;
        }

        if (srcUrl.search) {
            srcUrl.search += `&${queryString}`;
        } else {
            srcUrl.search += queryString;
        }

        if (skipWidthAndHeightSettings) {
            const params = srcUrl.searchParams;
            // remove w and h parameters from query string so the image is not adjusted with white background
            params.delete('w');
            params.delete('h');
        }

        return srcUrl.href;
    } catch (e) {
        return imageSrc;
    }
};

export const getSourceData = (
    src: string | undefined,
    gridSetting: IGridSettings,
    image: IImageData,
    imageSettings: IImageSettings,
    viewport: Viewport,
    largestGridSettingViewportName: Viewport
) => {
    if (!image || !src || !imageSettings || !imageSettings.viewports || !imageSettings.viewports[viewport]) {
        return;
    }

    const media = getMedia(gridSetting, imageSettings, viewport, largestGridSettingViewportName);
    if (!media) {
        return;
    }

    const srcSet = getImageResizeUrl(src, imageSettings.viewports[viewport], imageSettings);
    if (!srcSet) {
        return;
    }
    return {
        media,
        srcSet
    };
};

/**
 * This function reads the mode and background color values from ImageSettings and replaces the Image resizer query string
 * @param queryString ImageResizer query string
 * @param inputImageSettings Image Settings
 * @returns Updated ImageResizer query string with mode and background color
 */
const updateImageResizerQueryString = (queryString: string, inputImageSettings: IImageSettings): string => {
    const parsedQueryString = querystring.parse(queryString);
    if (inputImageSettings.mode) {
        parsedQueryString['m'] = inputImageSettings.mode.toString();
    }

    if (inputImageSettings.backgroundColor) {
        // Reading background color from site builder
        parsedQueryString['b'] = inputImageSettings.backgroundColor;

        // Converting image to jpg format, as background color in padding will only work if the image type is of format jpg,
        if (!parsedQueryString['f'] || parsedQueryString['f'] !== 'jpg') {
            parsedQueryString['f'] = 'jpg';
        }
    }
    return querystring.stringify(parsedQueryString);
};
